/**
 * Pinning somewhere to go.
 *
 * Search already answers "where is the nearest station with a large pad" — a
 * question about *kinds* of place, which is what you ask when you are looking
 * for work. It cannot answer "take me to Verath", which is what you ask when a
 * mission names a destination or a friend says where they are, and those are
 * the two reasons anybody actually crosses a galaxy.
 *
 * ── Everything reduces to a system ──────────────────────────────────────────
 *
 * Routing works in systems and only in systems, so a target is a system plus a
 * note about what within it you were really after. A cluster resolves to the
 * system nearest its centre; a body resolves to the system it orbits in. That
 * keeps `planRoute` unchanged and means the cockpit can ask one question —
 * "which system am I heading for" — regardless of what the player pointed at.
 *
 * ── Why bodies are searched differently ─────────────────────────────────────
 *
 * Cluster and system names come off the galaxy map, which is cheap by design —
 * seventeen thousand summaries with no contents built. A body's name does not
 * exist until its system is generated, so a galaxy-wide body index would mean
 * generating every system in the game to answer one keystroke. Body search is
 * therefore scoped to a set of systems the caller nominates, and the caller is
 * expected to nominate somewhere the player might plausibly mean.
 */
import { getGalaxyMap, getSystem } from './generate.js';
/** How many matches a search returns before it stops looking. */
export const NAME_SEARCH_LIMIT = 12;
/**
 * Rank a match so the obvious answer comes first.
 *
 * Exact beats prefix beats contained. Without this, typing a full name gives
 * you every system that merely has it as a substring in whatever order the
 * galaxy happens to be stored in, and the one you actually typed can be tenth.
 */
function score(name, query) {
    const haystack = name.toLowerCase();
    if (haystack === query)
        return 0;
    if (haystack.startsWith(query))
        return 1;
    if (haystack.includes(query))
        return 2;
    return -1;
}
/** The system nearest a cluster's centre, which is what a cluster resolves to. */
function heartOf(seed, clusterId) {
    const entry = getGalaxyMap(seed).find(({ cluster }) => cluster.id === clusterId);
    if (!entry)
        return null;
    let best = null;
    let bestGap = Infinity;
    for (const system of entry.systems) {
        const gap = Math.hypot(system.galacticPosition.x - entry.cluster.position.x, system.galacticPosition.y - entry.cluster.position.y, system.galacticPosition.z - entry.cluster.position.z);
        if (gap < bestGap) {
            bestGap = gap;
            best = system.id;
        }
    }
    return best;
}
/**
 * Clusters and systems whose names match.
 *
 * Both come off the galaxy map, so this is a scan of names in memory rather
 * than any generation. Systems are listed before clusters at equal quality: a
 * player typing a name is far more often after a place than after the region
 * containing it.
 */
export function searchNames(seed, query) {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2)
        return [];
    const found = [];
    for (const { cluster, systems } of getGalaxyMap(seed)) {
        for (const system of systems) {
            const rank = score(system.name, needle);
            if (rank < 0)
                continue;
            found.push({
                rank: rank * 2,
                target: {
                    kind: 'system',
                    label: system.name,
                    detail: `system in ${cluster.name}`,
                    systemId: system.id,
                    clusterId: cluster.id,
                },
            });
        }
        const rank = score(cluster.name, needle);
        if (rank < 0)
            continue;
        const heart = heartOf(seed, cluster.id);
        if (!heart)
            continue;
        found.push({
            rank: rank * 2 + 1,
            target: {
                kind: 'cluster',
                label: cluster.name,
                detail: `cluster · ${systems.length} systems`,
                systemId: heart,
                clusterId: cluster.id,
            },
        });
    }
    return found
        .sort((a, b) => a.rank - b.rank || a.target.label.localeCompare(b.target.label))
        .slice(0, NAME_SEARCH_LIMIT)
        .map(({ target }) => target);
}
/**
 * Named bodies inside a nominated set of systems.
 *
 * Generates each system it is given, so the caller decides how much work this
 * is — see the note at the top of the file. Everything a system contains is
 * searchable, because "meet me at the anomaly" is as reasonable a thing to say
 * as "meet me at the station".
 */
export function searchBodies(seed, query, within) {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2)
        return [];
    const found = [];
    for (const systemId of within) {
        const system = getSystem(seed, systemId);
        const add = (name, detail) => {
            const rank = score(name, needle);
            if (rank < 0)
                return;
            found.push({
                rank,
                target: {
                    kind: 'body',
                    label: name,
                    detail: `${detail} · ${system.name}`,
                    systemId,
                    clusterId: system.clusterId,
                    bodyName: name,
                },
            });
        };
        for (const planet of system.planets) {
            add(planet.name, planet.type);
            for (const moon of planet.moons)
                add(moon.name, `moon of ${planet.name}`);
        }
        for (const station of system.stations)
            add(station.name, 'station');
        for (const field of system.asteroidFields)
            add(field.name, 'asteroid field');
        for (const anomaly of system.anomalies)
            add(anomaly.name, 'anomaly');
        // Enough is enough. The caller nominated a scope; if the answer is not in
        // it, widening the scope is their decision rather than this one's.
        if (found.length >= NAME_SEARCH_LIMIT * 2)
            break;
    }
    return found
        .sort((a, b) => a.rank - b.rank || a.target.label.localeCompare(b.target.label))
        .slice(0, NAME_SEARCH_LIMIT)
        .map(({ target }) => target);
}
//# sourceMappingURL=navTarget.js.map